import { enrollMail, type NewMailEvent } from '@/lib/mail'
import type { InboxCounts } from '@/lib/inbox'
import { fetchPulseServices } from '@/lib/pulse'
import type { StatusSnapshot } from '@/lib/status'
import type { ThinkingEvent } from '@/lib/thinking'
import {
  applyFeedState,
  applyInboxCounts,
  applyMailCounts,
  applyMailNew,
  applyPulse,
  applyStatus,
  applyThinking,
  applyTodos,
  applyVoice,
  applyVoiceLink,
  initialTelemetryState,
  pruneTelemetry,
  type TelemetryState,
  type VoiceEvent,
} from './core'

/** localStorage key holding the TovOS voice-bridge SSE read token. */
const VOICE_KEY_LS = 'v2:tovos-voice-key'
/** One-time unlock query param: `?tovoskey=<read-token>` stores + strips. */
const VOICE_KEY_PARAM = 'tovoskey'

/**
 * Read the stored voice read-key, consuming a one-time `?tovoskey=` unlock
 * first. If the URL carries `?tovoskey=<k>` we persist it to localStorage and
 * strip it from the address bar (history.replaceState) so the secret never
 * lingers in the URL or browser history. Safe to call repeatedly + SSR-safe.
 * Returns the active key, or null when none is set (voice stays dormant).
 */
export function bootstrapVoiceKey(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const url = new URL(window.location.href)
    const fromUrl = url.searchParams.get(VOICE_KEY_PARAM)
    if (fromUrl) {
      window.localStorage.setItem(VOICE_KEY_LS, fromUrl)
      url.searchParams.delete(VOICE_KEY_PARAM)
      const qs = url.searchParams.toString()
      window.history.replaceState(null, '', url.pathname + (qs ? `?${qs}` : '') + url.hash)
    }
    return window.localStorage.getItem(VOICE_KEY_LS)
  } catch {
    return null
  }
}

/**
 * Telemetry transport (R4): the ONE shell-level subscription to the
 * existing live feeds — thinking bus, inbox SSE, mail SSE — plus the
 * 30s status poll and 60s todos poll. Rail badges, the HUD and (from
 * U5) flow maps all read this store instead of opening their own
 * EventSources. Pulse heartbeats (U6) compose in client-side later.
 *
 * Note: the audit-jobs SSE turned out to be per-job and IP-allowlisted
 * (/audit-admin/api/job/:id/events) — not an ambient feed. The U12
 * Audits embed consumes it inside the admin app instead.
 */
const STATUS_POLL_MS = 30_000
/** Pulse fleet refresh — operator wants beats visibly fresh (10–30s). */
const PULSE_POLL_MS = 15_000
const TODOS_POLL_MS = 60_000
const PRUNE_MS = 5_000

function createTelemetryStore() {
  let state: TelemetryState = initialTelemetryState()
  let started = false
  const listeners = new Set<() => void>()
  /** Wall-clock ms of the last *live* voice event — the sim reads this to
   *  decide whether the bridge is silent (R13). 0 = none seen yet. */
  let lastLiveVoiceAt = 0

  function set(next: TelemetryState) {
    state = next
    listeners.forEach((l) => l())
  }

  /** Feed a voice event through the shared reducer. `live` flags an event
   *  from the real SSE (vs. the client sim) so the sim can stand down when
   *  the bridge is actually talking. */
  function pushVoice(ev: VoiceEvent, live: boolean) {
    if (live) lastLiveVoiceAt = Date.now()
    set(applyVoice(state, ev, Date.now()))
  }

  function start() {
    if (started || typeof window === 'undefined') return
    started = true

    // ── thinking bus ────────────────────────────────────────────────
    const thinkingEs = new EventSource('/api/llm/events')
    thinkingEs.addEventListener('hello', () => set(applyFeedState(state, 'live')))
    thinkingEs.addEventListener('agent.thinking', (e) => {
      try {
        const ev = JSON.parse((e as MessageEvent).data) as ThinkingEvent
        set(applyThinking(applyFeedState(state, 'live'), ev, Date.now()))
      } catch {
        /* bad payload */
      }
    })
    thinkingEs.onerror = () => set(applyFeedState(state, 'lost'))

    // ── inbox events ────────────────────────────────────────────────
    const inboxEs = new EventSource('/api/inbox/events')
    inboxEs.addEventListener('counts', (e) => {
      try {
        const counts = JSON.parse((e as MessageEvent).data) as InboxCounts
        set(applyInboxCounts(state, counts.unread))
      } catch {
        /* ignore */
      }
    })

    // ── mail events (enroll first, like useMailEvents) ──────────────
    enrollMail()
      .then(() => {
        const mailEs = new EventSource('/api/mail/events')
        mailEs.addEventListener('counts', (e) => {
          try {
            set(applyMailCounts(state, JSON.parse((e as MessageEvent).data) as Record<string, number>))
          } catch {
            /* ignore */
          }
        })
        mailEs.addEventListener('mail:new', (e) => {
          try {
            set(applyMailNew(state, JSON.parse((e as MessageEvent).data) as NewMailEvent))
          } catch {
            /* ignore */
          }
        })
      })
      .catch(() => {
        /* mail enrollment failed — tile stays at "--" */
      })

    // ── voice bridge (TovOS U5 — the ONE voice socket, hard rule #8) ──
    // GET /api/tovos/voice/events is now GATED: it 401s without a read key
    // and streams the MINIMIZED `event: tovos.voice` payload ({ kind, intent?,
    // target?, label, source, at } — NO raw transcript). The key lives in
    // localStorage['v2:tovos-voice-key'] (set via a one-time ?tovoskey= unlock,
    // see bootstrapVoiceKey). With NO key we DON'T open the socket at all
    // (voice dormant, no 401 spam) and the indicator reads "locked"; the other
    // three telemetry feeds are unaffected. Mirrors the thinking-bus error
    // handling; failure is silent (AE4).
    const voiceKey = bootstrapVoiceKey()
    if (voiceKey) {
      set(applyVoiceLink(state, 'connecting'))
      const voiceEs = new EventSource(
        `/api/tovos/voice/events?key=${encodeURIComponent(voiceKey)}`,
      )
      // The bridge greets with `event: hello` on connect — proof the keyed
      // stream is open even before any voice moment arrives.
      voiceEs.addEventListener('hello', () => set(applyVoiceLink(state, 'online')))
      voiceEs.addEventListener('tovos.voice', (e) => {
        try {
          const ev = JSON.parse((e as MessageEvent).data) as VoiceEvent
          if (ev && typeof ev.kind === 'string') {
            pushVoice(ev, true)
            set(applyVoiceLink(state, 'online'))
          }
        } catch {
          /* bad payload — ignore, never surface an error (AE4) */
        }
      })
      voiceEs.onerror = () => {
        /* bridge down: no error UI; indicator stays at its last link state
           (the EventSource auto-reconnects). A bad/expired key 401s here too —
           silent, never a console-spamming reopen loop beyond the browser's
           own backoff. */
      }
    }

    // ── polls ───────────────────────────────────────────────────────
    const pollStatus = () => {
      fetch('/api/status', { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j: StatusSnapshot | null) => {
          if (j) set(applyStatus(state, j))
        })
        .catch(() => {
          /* keep last health */
        })
    }
    const pollTodos = () => {
      fetch('/api/todos', { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j: { open?: number } | null) => {
          if (j && typeof j.open === 'number') set(applyTodos(state, j.open))
        })
        .catch(() => {
          /* keep last */
        })
    }
    const pollPulse = () => {
      fetchPulseServices()
        .then((services) => set(applyPulse(state, services)))
        .catch(() => {
          /* keep last fleet view */
        })
    }
    pollStatus()
    pollTodos()
    pollPulse()
    window.setInterval(pollStatus, STATUS_POLL_MS)
    window.setInterval(pollTodos, TODOS_POLL_MS)
    window.setInterval(pollPulse, PULSE_POLL_MS)

    // ── prune ticker (session windows, runaway runs) ────────────────
    window.setInterval(() => set(pruneTelemetry(state, Date.now())), PRUNE_MS)
  }

  return {
    getState: () => state,
    subscribe(l: () => void): () => void {
      listeners.add(l)
      return () => {
        listeners.delete(l)
      }
    },
    start,
    /** Push a voice event through the shared reducer (used by the client
     *  sim — `live=false` — so simulated and real events share one path). */
    pushVoice,
    /** Wall-clock ms of the last LIVE voice event (0 = none). The sim
     *  reads this to stand down while the real bridge is active. */
    getLastLiveVoiceAt: () => lastLiveVoiceAt,
  }
}

export const telemetryStore = createTelemetryStore()
